import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { apiJson } from '@/lib/api';
import type { ProfessionalCodeBatchCreateRequest, ProfessionalCodeBatchDetailResponse } from '@/lib/types';

type AdminProfessionalCodesListResponse = {
  total: number;
  batches: Array<{
    id: number;
    created_at: string;
    professional_user_id: number;
    label: string | null;
    grant_source: string;
    trial_days: number;
    total_codes: number;
    available_count: number;
    redeemed_count: number;
    revoked_count: number;
    expired_count: number;
  }>;
};

function AdminProfessionalCodesPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [professionalUserId, setProfessionalUserId] = useState('');
  const [q, setQ] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [createForUserId, setCreateForUserId] = useState('');
  const [createCount, setCreateCount] = useState(5);
  const [createTrialDays, setCreateTrialDays] = useState(90);
  const [createLabel, setCreateLabel] = useState('');

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (professionalUserId.trim()) p.set('professional_user_id', professionalUserId.trim());
    if (q.trim()) p.set('q', q.trim());
    p.set('limit', '100');
    return p.toString();
  }, [professionalUserId, q]);

  const listQuery = useQuery({
    queryKey: ['admin-professional-codes', params],
    queryFn: () =>
      apiJson<AdminProfessionalCodesListResponse>(
        getToken,
        `/v1/admin/professional-codes/batches?${params}`,
      ),
  });

  const detailQuery = useQuery({
    queryKey: ['admin-professional-code-batch', selectedBatchId],
    queryFn: () =>
      apiJson<ProfessionalCodeBatchDetailResponse>(
        getToken,
        `/v1/admin/professional-codes/batches/${selectedBatchId}`,
      ),
    enabled: !!selectedBatchId,
  });

  const revokeBatchMutation = useMutation({
    mutationFn: (batchId: number) =>
      apiJson(getToken, `/v1/admin/professional-codes/batches/${batchId}/revoke`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-professional-codes'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-professional-code-batch'] });
    },
  });

  const revokeCodeMutation = useMutation({
    mutationFn: (codeId: number) =>
      apiJson(getToken, `/v1/admin/professional-codes/${codeId}/revoke`, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-professional-codes'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-professional-code-batch'] });
    },
  });

  const createBatchMutation = useMutation({
    mutationFn: () => {
      const payload: ProfessionalCodeBatchCreateRequest = {
        count: createCount,
        label: createLabel.trim() || undefined,
        trial_days: createTrialDays,
        default_share_chats: true,
        default_share_expenses: true,
        default_share_custody_calendar: true,
        default_share_journal: true,
      };
      return apiJson<ProfessionalCodeBatchDetailResponse>(
        getToken,
        `/v1/admin/professional-codes/batches?professional_user_id=${encodeURIComponent(createForUserId.trim())}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
    },
    onSuccess: async (data) => {
      setSelectedBatchId(data.batch.id);
      await queryClient.invalidateQueries({ queryKey: ['admin-professional-codes'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-professional-code-batch'] });
    },
  });

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-semibold text-gray-900'>Professional Codes</h1>
        <p className='text-sm text-gray-500 mt-1'>
          View, grant, and revoke professional invite codes.
        </p>
      </div>

      <div className='rounded-xl border border-gray-200 bg-white p-4 space-y-3'>
        <h2 className='text-sm font-semibold text-gray-900'>Filters</h2>
        <div className='grid gap-3 sm:grid-cols-2'>
          <label className='text-xs font-semibold text-gray-600'>
            Professional user id
            <input
              className='mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm'
              value={professionalUserId}
              onChange={(e) => setProfessionalUserId(e.target.value)}
              placeholder='e.g. 123'
            />
          </label>
          <label className='text-xs font-semibold text-gray-600'>
            Batch label search
            <input
              className='mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm'
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='e.g. starter'
            />
          </label>
        </div>
      </div>

      <div className='rounded-xl border border-gray-200 bg-white p-4 space-y-3'>
        <h2 className='text-sm font-semibold text-gray-900'>Grant batch</h2>
        <div className='grid gap-3 sm:grid-cols-4'>
          <input
            className='rounded-md border border-gray-300 px-3 py-2 text-sm'
            value={createForUserId}
            onChange={(e) => setCreateForUserId(e.target.value)}
            placeholder='professional user id'
          />
          <input
            className='rounded-md border border-gray-300 px-3 py-2 text-sm'
            value={createCount}
            min={1}
            type='number'
            onChange={(e) => setCreateCount(Number(e.target.value || 5))}
            placeholder='count'
          />
          <input
            className='rounded-md border border-gray-300 px-3 py-2 text-sm'
            value={createTrialDays}
            min={1}
            type='number'
            onChange={(e) => setCreateTrialDays(Number(e.target.value || 90))}
            placeholder='trial days'
          />
          <input
            className='rounded-md border border-gray-300 px-3 py-2 text-sm'
            value={createLabel}
            onChange={(e) => setCreateLabel(e.target.value)}
            placeholder='label (optional)'
          />
        </div>
        <button
          type='button'
          onClick={() => createBatchMutation.mutate()}
          disabled={createBatchMutation.isPending || !createForUserId.trim()}
          className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50'
        >
          {createBatchMutation.isPending ? 'Granting…' : 'Grant codes'}
        </button>
      </div>

      <div className='rounded-xl border border-gray-200 bg-white overflow-hidden'>
        <table className='min-w-full text-sm'>
          <thead className='bg-gray-50 text-gray-600 uppercase text-xs tracking-wide'>
            <tr>
              <th className='px-4 py-3 text-left'>Batch</th>
              <th className='px-4 py-3 text-left'>Professional</th>
              <th className='px-4 py-3 text-left'>Counts</th>
              <th className='px-4 py-3 text-left'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(listQuery.data?.batches ?? []).map((batch) => (
              <tr key={batch.id} className='border-t border-gray-100'>
                <td className='px-4 py-3'>
                  <p className='font-medium text-gray-900'>{batch.label || `Batch #${batch.id}`}</p>
                  <p className='text-xs text-gray-500'>
                    {batch.grant_source} · {new Date(batch.created_at).toLocaleDateString()}
                  </p>
                </td>
                <td className='px-4 py-3 text-gray-700'>#{batch.professional_user_id}</td>
                <td className='px-4 py-3 text-gray-700'>
                  {batch.available_count} avail · {batch.redeemed_count} redeemed · {batch.revoked_count}{' '}
                  revoked · {batch.expired_count} expired
                </td>
                <td className='px-4 py-3'>
                  <div className='flex flex-wrap gap-2'>
                    <button
                      type='button'
                      onClick={() => setSelectedBatchId(batch.id)}
                      className='rounded-md border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-700'
                    >
                      View
                    </button>
                    <button
                      type='button'
                      onClick={() => revokeBatchMutation.mutate(batch.id)}
                      disabled={revokeBatchMutation.isPending}
                      className='rounded-md border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 disabled:opacity-50'
                    >
                      Revoke unused
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!listQuery.isLoading && (listQuery.data?.batches ?? []).length === 0 ? (
              <tr>
                <td colSpan={4} className='px-4 py-6 text-center text-sm text-gray-500'>
                  No code batches found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {selectedBatchId ? (
        <div className='rounded-xl border border-gray-200 bg-white p-4'>
          <h2 className='text-sm font-semibold text-gray-900 mb-2'>Batch detail</h2>
          {detailQuery.isLoading ? (
            <p className='text-sm text-gray-500'>Loading…</p>
          ) : (
            <ul className='space-y-2'>
              {(detailQuery.data?.codes ?? []).map((code) => (
                <li
                  key={code.id}
                  className='rounded-lg border border-gray-200 px-3 py-2 flex flex-wrap items-center justify-between gap-2'
                >
                  <div>
                    <p className='font-mono text-sm text-gray-900'>{code.code}</p>
                    <p className='text-xs text-gray-500'>
                      {code.status}
                      {code.redeemed_at
                        ? ` · redeemed ${new Date(code.redeemed_at).toLocaleDateString()}`
                        : ''}
                    </p>
                  </div>
                  {code.status === 'available' ? (
                    <button
                      type='button'
                      onClick={() => revokeCodeMutation.mutate(code.id)}
                      disabled={revokeCodeMutation.isPending}
                      className='rounded-md border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 disabled:opacity-50'
                    >
                      Revoke
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/professional-codes')({
  component: AdminProfessionalCodesPage,
});
